export interface ApiErrorInfo {
  message: string;
  // These are genuinely optional-or-undefined: several code paths compute them
  // as `value | undefined`, so `| undefined` is the accurate declaration.
  code?: string | undefined;
  details?: string | undefined;
  traceParent?: string | undefined;
  source: "Backend API" | "Frontend" | "Unknown";
}

interface BackendErrorResponse {
  message?: string;
  error?: string;
  code?: string;
  details?: string;
  traceParent?: string;
  _embedded?: {
    errors?: Array<{
      message?: string;
      path?: string;
      logref?: string;
      details?: string;
      suggestion?: string;
    }>;
  };
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
async function parseBackendResponse(
  response: Response,
): Promise<ApiErrorInfo | undefined> {
  try {
    const responseText = await response.clone().text();
    let responseData: BackendErrorResponse;
    try {
      responseData = JSON.parse(responseText);
    } catch {
      return {
        message: `HTTP ${response.status}: ${response.statusText}`,
        source: "Backend API",
      };
    }

    if (
      responseData &&
      typeof responseData === "object" &&
      responseData.code &&
      responseData.message
    ) {
      return {
        message: responseData.message,
        code: responseData.code,
        details: responseData.details,
        traceParent: responseData.traceParent,
        source: "Backend API",
      };
    }

    const backendError = responseData?._embedded?.errors?.[0];
    if (backendError) {
      const detailParts: string[] = [];
      if (backendError.path) detailParts.push(`Path: ${backendError.path}`);
      if (backendError.logref)
        detailParts.push(`Logref: ${backendError.logref}`);
      if (backendError.details)
        detailParts.push(`Details: ${backendError.details}`);
      if (backendError.suggestion)
        detailParts.push(`Suggestion: ${backendError.suggestion}`);

      return {
        message: backendError.message || "Server error occurred",
        details: detailParts.length > 0 ? detailParts.join("\n") : undefined,
        source: "Backend API",
      };
    }

    if (responseData?.message) {
      return {
        message: responseData.message,
        details: `Status: ${response.status || "N/A"}`,
        source: "Backend API",
      };
    }

    // Some controllers return the human message under an "error" key,
    // e.g. HttpResponse.badRequest(mapOf("error" to e.message)).
    if (responseData?.error && typeof responseData.error === "string") {
      return {
        message: responseData.error,
        details: `Status: ${response.status || "N/A"}`,
        source: "Backend API",
      };
    }

    return {
      message: `HTTP ${response.status}: ${response.statusText}`,
      source: "Backend API",
    };
  } catch {
    return {
      message: `HTTP ${response.status}: ${response.statusText}`,
      source: "Backend API",
    };
  }
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export async function parseApiError(error: unknown): Promise<ApiErrorInfo> {
  if (error && typeof error === "object") {
    // Check for HTTP response errors (ResponseError from generated client)
    if ("response" in error && error.response instanceof Response) {
      const backendError = await parseBackendResponse(error.response);
      if (backendError) {
        return backendError;
      }
      return {
        message: `HTTP ${error.response.status}: ${error.response.statusText}`,
        source: "Backend API",
      };
    }

    // ResponseError without a proper Response object
    if ("name" in error && error.name === "ResponseError") {
      return {
        message: "Server error occurred - Backend threw an exception",
        details: `Error type: ${error.name}\nMessage: ${"message" in error ? error.message : "No message"}\n\nThis suggests the backend threw an exception before sending a proper response. Check your backend logs for the actual error.`,
        source: "Backend API",
      };
    }

    // Known frontend error types — specific messages aid debugging
    if ("name" in error && typeof error.name === "string") {
      switch (error.name) {
        case "FetchError":
          return {
            message: "Network connection failed - Unable to reach the server",
            details:
              "This usually indicates a network connectivity issue or the server is unreachable.",
            source: "Frontend",
          };
        case "TypeError":
          return {
            message: "Type or validation error occurred",
            details: "message" in error ? String(error.message) : undefined,
            source: "Frontend",
          };
        case "ReferenceError":
          return {
            message: "Reference error - Undefined variable or function",
            details: "message" in error ? String(error.message) : undefined,
            source: "Frontend",
          };
        case "SyntaxError":
          return {
            message: "Syntax error in code",
            details: "message" in error ? String(error.message) : undefined,
            source: "Frontend",
          };
      }
    }

    // Standard Error objects
    if (error instanceof Error) {
      const details = error.cause
        ? `Cause: ${"message" in (error.cause as object) ? (error.cause as Error).message : String(error.cause)}`
        : error.stack;
      return {
        message: error.message,
        details,
        source: "Frontend",
      };
    }

    // Objects with a message property
    if ("message" in error && typeof error.message === "string") {
      return {
        message: error.message,
        source: "Frontend",
      };
    }
  }

  if (typeof error === "string") {
    return {
      message: error,
      source: "Frontend",
    };
  }

  return {
    message: "An unexpected error occurred",
    source: "Unknown",
  };
}
